import React, { useMemo, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../Elements/Button/Button";
import { useAuthentication } from "../../../Hooks/useAuthentication";
import useStore from "../../../useStore";
import { fetchOrdersByRestaurant } from "../../../Services/fetchGetOrdersByRestaurant"; // 👈 ya existe en web
import "./userActions.css";
import useGeolocation from "../../../Hooks/useGeolocation";
import { annotateWithDistance, filterByRadius, isValidCoord, RADIUS_KM_ADMINISTRADORES } from "../../../Utilities/geocode";

const UserActions = () => {
  const { isAuthenticated, role, userId, removeToken } = useAuthentication();
  const navigate = useNavigate();

  const { fetchDataGetMyRestaurants, restaurants,
    fetchDataGetOrdersForCourier, courierOrders,
    myCourierOrders, fetchDataCourierMyOrders,
    inactiveRestaurants, fetchDataInactiveRestaurants,
    inactiveUsers, fetchDataInactiveUsers
  } = useStore();


  const [hasPending, setHasPending] = useState(false);
  const [checkingPending, setCheckingPending] = useState(false);
  const { status: geoStatus, coords } = useGeolocation();

  const availableToTakeCount = useMemo(() => {
    const list = Array.isArray(courierOrders) ? courierOrders : [];
    return list.filter((o) => {
      const assigned = (o?.repartidorAsignado ?? "").toString().trim();
      const status = String(o?.status ?? "").trim().toLowerCase();
      return (!assigned || assigned === "") && status === "preparando";
    }).length;
  }, [courierOrders]);

  const toDeliverCount = useMemo(() => {
    const list = Array.isArray(myCourierOrders) ? myCourierOrders : [];
    return list.filter(o => String(o?.status ?? "").trim().toLowerCase() === "enviado").length;
  }, [myCourierOrders]);

  const normalizedRole = useMemo(
    () => (role ? String(role).toLowerCase() : null),
    [role]
  );

  const inactiveUsersNearbyCount = useMemo(() => {
    const arr = Array.isArray(inactiveUsers) ? inactiveUsers : [];
    // Si el admin dio permiso y hay coords válidas, contamos solo los cercanos
    if (geoStatus === "granted" && coords && isValidCoord(coords.lat, coords.lng)) {
      const candidates = arr
        .map(u => ({ ...u, lat: u?.delivery?.lat, lng: u?.delivery?.lng }))
        .filter(u => isValidCoord(u.lat, u.lng));
      const withDist = annotateWithDistance({ lat: coords.lat, lng: coords.lng }, candidates);
      return filterByRadius(withDist, RADIUS_KM_ADMINISTRADORES).length;
    }
    // Si no hay geoloc, cuenta total
    return arr.length;
  }, [inactiveUsers, geoStatus, coords]);

  // arriba, define un handler
  const openMyRestaurants = () => {
    setHasPending(false);
    navigate("/restaurants/get-my-restaurants");
  };

  const handleLogout = () => {
    removeToken();
    navigate("/");
    window.location.reload();
  };

  const isAdmin = normalizedRole === "administrador" || normalizedRole === "admin";
  const isComerciante = normalizedRole === "comerciante";
  const isRepartidor = normalizedRole === "repartidor";

  useEffect(() => {
    if (!isAuthenticated || !(isRepartidor || isAdmin)) return;
    const run = () => fetchDataGetOrdersForCourier?.();
    run();
    const iv = setInterval(run, 15000);
    return () => clearInterval(iv);
  }, [isAuthenticated, isRepartidor, isAdmin, fetchDataGetOrdersForCourier]);


  const badgeBubbleRing = {
    position: "absolute", top: -8, right: -8, width: 24, height: 24,
    borderRadius: "999px", backgroundColor: "rgba(99,102,241,0.18)", zIndex: 10
  };
  const badgeBubble = {
    position: "absolute", top: -10, right: -10, minWidth: 20, height: 20,
    padding: "0 6px", borderRadius: "999px", backgroundColor: "#ef4444",
    border: "2px solid #fff", display: "flex", alignItems: "center",
    justifyContent: "center", zIndex: 20, boxShadow: "0 2px 6px rgba(239,68,68,0.6)"
  };
  const badgeBubbleText = { color: "#fff", fontWeight: 900, fontSize: 11 };

  useEffect(() => {
    const onFocus = () => {
      fetchDataGetOrdersForCourier?.();
      fetchDataCourierMyOrders?.();
      fetchDataInactiveRestaurants?.();
      fetchDataInactiveUsers?.();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [fetchDataGetOrdersForCourier, fetchDataCourierMyOrders, fetchDataInactiveRestaurants, fetchDataInactiveUsers]);

  useEffect(() => {
    if (!isAuthenticated || !isAdmin) return;
    const run = () => fetchDataInactiveRestaurants?.();
    run();
    const iv = setInterval(run, 30000);
    return () => clearInterval(iv);
  }, [isAuthenticated, isAdmin, fetchDataInactiveRestaurants]);
  const inactiveNearbyCount = useMemo(() => {
    const arr = Array.isArray(inactiveRestaurants) ? inactiveRestaurants : [];
    if (geoStatus === "granted" && coords && isValidCoord(coords.lat, coords.lng)) {
      const candidates = arr.filter((r) => isValidCoord(r.lat, r.lng));
      const withDist = annotateWithDistance({ lat: coords.lat, lng: coords.lng }, candidates);
      return filterByRadius(withDist, RADIUS_KM_ADMINISTRADORES).length;
    }
    return arr.length;
  }, [inactiveRestaurants, geoStatus, coords]);
  useEffect(() => {
    if (!isAuthenticated || !(isRepartidor || isAdmin)) return;
    const run = () => fetchDataCourierMyOrders?.();
    run();
    const iv = setInterval(run, 15000);
    return () => clearInterval(iv);
  }, [isAuthenticated, isRepartidor, isAdmin, fetchDataCourierMyOrders]);

  useEffect(() => {
    let cancel = false;

    const run = async () => {
      if (!isAuthenticated || !(isComerciante || isAdmin) || !userId) {
        if (!cancel) {
          setHasPending(false);
          setCheckingPending(false);
        }
        return;
      }

      setCheckingPending(true);

      try {
        let myRestaurants = Array.isArray(restaurants) ? restaurants : [];

        if (myRestaurants.length === 0) {
          const loaded = await fetchDataGetMyRestaurants();
          if (Array.isArray(loaded)) {
            myRestaurants = loaded;
          }
        }

        const mine = myRestaurants.filter(
          (r) => String(r?.createdByUserId ?? "") === String(userId)
        );

        if (mine.length === 0) {
          if (!cancel) {
            setHasPending(false);
            setCheckingPending(false);
          }
          return;
        }

        const ids = mine
          .map((r) => String(r?.restauranteId ?? r?.id ?? ""))
          .filter(Boolean);

        const results = await Promise.allSettled(
          ids.map((id) => fetchOrdersByRestaurant(id))
        );

        const ALERT = new Set(["pendiente", "preparando"]);
        let found = false;

        for (const r of results) {
          if (r.status !== "fulfilled") continue;
          const items = Array.isArray(r.value?.items) ? r.value.items : [];
          if (
            items.some((o) =>
              ALERT.has(String(o?.status ?? "").trim().toLowerCase())
            )
          ) {
            found = true;
            break;
          }
        }

        if (!cancel) setHasPending(found);
      } catch {
        if (!cancel) setHasPending(false);
      } finally {
        if (!cancel) setCheckingPending(false);
      }
    };

    run();

    return () => {
      cancel = true;
    };

    // 👇 Esto solo silencia el warning, no cambia el comportamiento
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isComerciante, isAdmin, userId, fetchDataGetMyRestaurants]);

  const wrap = { position: "relative", display: "inline-block" };
  const dotRing = {
    position: "absolute", top: -8, right: -8, width: 22, height: 22,
    borderRadius: "999px", backgroundColor: "rgba(239,68,68,0.14)", zIndex: 10
  };
  const dot = {
    position: "absolute", top: -4, right: -4, width: 14, height: 14,
    borderRadius: "999px", backgroundColor: "#ef4444",
    border: "2px solid #fff", zIndex: 20, boxShadow: "0 2px 6px rgba(239,68,68,0.7)"
  };
  const dotChk = {
    position: "absolute", top: -4, right: -4, width: 12, height: 12,
    borderRadius: "999px", backgroundColor: "#9ca3af",
    border: "2px solid #fff", zIndex: 20, boxShadow: "0 2px 5px rgba(156,163,175,0.5)"
  };

  if (!isAuthenticated) return null;

  return (
    <div className="user-actions d-flex flex-wrap gap-2">
      {(isComerciante || isAdmin) && (
        <>
          <Button
            text="Agregar comercio"
            color="blue"
            fill
            classNames="home-add-btn"
            action={() => navigate("/add-restaurants")}
          />

          {/* 👇 dot igual que en la app */}
          <div style={wrap}>
            <Button
              text="Ver mis Comercios"
              color="green"
              fill
              classNames="home-add-btn"
              action={openMyRestaurants}
            />
            {hasPending && !checkingPending && (<><span style={dotRing} /><span style={dot} /></>)}
            {checkingPending && !hasPending && (<span style={dotChk} />)}
          </div>
        </>
      )}

      {(isRepartidor || isAdmin) && (
        <div style={wrap}>
          <Button
            text="¿Que hay para entregar?"
            color="orange"
            fill
            classNames="home-add-btn"
            action={() => navigate("/courier/orders")}
          />
          {availableToTakeCount > 0 ? (
            <>
              <span style={badgeBubbleRing} />
              <span style={badgeBubble}>
                <span style={badgeBubbleText}>
                  {availableToTakeCount > 99 ? "99" : availableToTakeCount}
                </span>
              </span>
            </>
          ) : (toDeliverCount > 0) ? (
            <>
              <span style={dotRing} />
              <span style={dot} />
            </>
          ) : null}
        </div>
      )}

      {isAdmin && (
        <>
          <div style={wrap}>
            <Button
              text="Activar Comercios"
              color="brown"
              fill
              classNames="home-add-btn"
              action={() => navigate("/restaurants/inactive")}
            />
            {inactiveNearbyCount > 0 && (
              <>
                <span style={badgeBubbleRing} />
                <span style={badgeBubble}>
                  <span style={badgeBubbleText}>{inactiveNearbyCount > 99 ? "99+" : inactiveNearbyCount}</span>
                </span>
              </>
            )}
          </div>
          <div style={wrap}>
            <Button
              text="Activar Usuarios"
              color="purple"
              fill
              classNames="home-add-btn"
              action={() => navigate("/users/inactive")}
            />
            {inactiveUsersNearbyCount > 0 && (
              <>
                <span style={badgeBubbleRing} />
                <span style={badgeBubble}>
                  <span style={badgeBubbleText}>
                    {inactiveUsersNearbyCount > 99 ? "99+" : inactiveUsersNearbyCount}
                  </span>
                </span>
              </>
            )}
          </div>
          <div style={wrap}>
            <Button
              text="Pagos"
              color="green"
              fill
              classNames="home-add-btn"
              action={() => navigate("/admin/reports/pagos")}
            />
          </div>
        </>
      )}

      {userId && (
        <Button
          text="Ver mis Pedidos"
          color="cyan"
          fill
          classNames="home-add-btn"
          action={() => navigate(`/users/${encodeURIComponent(userId)}/orders`)}
        />
      )}

      <Button
        text="Cerrar sesión"
        color="red"
        fill
        action={handleLogout}
        classNames="logout-btn"
      />
    </div>
  );
};

export default UserActions;